import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  FaPlus,
  FaTrashAlt,
  FaSyncAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaShieldAlt,
  FaUserSecret,
  FaInstagram,
  FaFacebook,
  FaPlay,
} from 'react-icons/fa';
import {
  TechSection,
  TechInput,
  TechSelect,
  TechButton,
  TechButtonGhost,
  TechButtonDanger,
  TechChip,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  Empty,
  Loading,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import {
  listDummyAccounts,
  createDummyAccount,
  deleteDummyAccount,
  testDummyAccountLogin,
  setDummyAccountSessionid,
} from '../../services/dummyAccountsService';
import { FaKey } from 'react-icons/fa';

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols || 4}, 1fr)`};
  gap: 0.55rem;
  margin-bottom: 0.85rem;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Card = styled.article`
  display: grid;
  grid-template-columns: 30px 1fr auto auto;
  gap: 0.7rem;
  align-items: center;
  padding: 0.7rem 0.85rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${({ $color }) => $color || accent};
  border-radius: 5px;
  margin-bottom: 0.45rem;

  .icon {
    width: 30px;
    height: 30px;
    border-radius: 6px;
    background: ${({ $color }) => `${$color}22` || 'rgba(0,180,216,0.1)'};
    color: ${({ $color }) => $color || accent};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
  }

  .name {
    color: var(--text-primary);
    font-size: 0.92rem;
    font-weight: 500;
    margin: 0;
  }

  .meta {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-top: 2px;

    .ok { color: var(--alert-success, #38d39f); }
    .err { color: var(--alert-error, #ff5470); }
    .warn { color: var(--alert-warning, #f59e0b); }
  }

  .actions {
    display: flex;
    gap: 4px;
  }
`;

const PLATFORM_META = {
  instagram: { color: '#E1306C', icon: <FaInstagram />, label: 'Instagram' },
  facebook:  { color: '#1877F2', icon: <FaFacebook />, label: 'Facebook' },
};

const STATUS_META = {
  active:              { tone: 'ok',   label: 'Activa' },
  failed:              { tone: 'err',  label: 'Fallo login' },
  challenge_required:  { tone: 'warn', label: 'Challenge IG' },
  banned:              { tone: 'err',  label: 'Baneada' },
  inactive:            { tone: 'warn', label: 'Inactiva' },
};

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  const days = h / 24;
  if (days < 30) return `hace ${Math.round(days)}d`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

const DummyAccountsPanel = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [form, setForm] = useState({ platform: 'instagram', username: '', password: '', display_name: '', notes: '' });

  const load = () => {
    setLoading(true);
    listDummyAccounts()
      .then((a) => setAccounts(a))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.username.trim()) return;
    setBusy(true);
    try {
      await createDummyAccount({
        platform: form.platform,
        username: form.username.trim().replace(/^@/, ''),
        password: form.password || null,
        display_name: form.display_name || null,
        notes: form.notes || null,
      });
      setForm({ ...form, username: '', password: '', display_name: '', notes: '' });
      load();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar la cuenta dummy?')) return;
    await deleteDummyAccount(id);
    load();
  };

  const handleSetSessionid = async (acc) => {
    const help = `Para sacar el sessionid de @${acc.username}:

1. Abrí instagram.com en browser (mejor incognito).
2. Iniciá sesión con la cuenta.
3. F12 → Application → Cookies → instagram.com
4. Copia el value de la fila "sessionid".

Pegalo acá:`;
    const sessionid = window.prompt(help, '');
    if (!sessionid) return;
    try {
      await setDummyAccountSessionid(acc.id, sessionid.trim());
      alert('Sessionid cargado. La cuenta queda active.');
      load();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Error');
    }
  };

  const handleTest = async (acc) => {
    setTestingId(acc.id);
    try {
      const result = await testDummyAccountLogin(acc.id);
      if (result.ok) {
        alert(`Login OK · sessionid generado (${result.sessionid_preview})`);
      } else {
        alert(`Login fallido (${result.status}): ${result.error}`);
      }
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Error');
    } finally {
      setTestingId(null);
      // Siempre refrescar: tanto en ok como en error el backend ya actualizó status/last_error.
      load();
    }
  };

  const totals = {
    total: accounts.length,
    active: accounts.filter((a) => a.status === 'active').length,
    withSession: accounts.filter((a) => a.has_sessionid).length,
    needsAttention: accounts.filter((a) => ['failed', 'challenge_required', 'banned'].includes(a.status)).length,
  };

  if (loading) return <Loading>Cargando cuentas dummy</Loading>;

  return (
    <>
      <StatStrip min="170px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaUserSecret /> Total</StatLabel>
          <StatValue>{totals.total}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel><FaCheckCircle /> Activas</StatLabel>
          <StatValue>{totals.active}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel><FaShieldAlt /> Con sessionid</StatLabel>
          <StatValue>{totals.withSession}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel><FaExclamationTriangle /> Requieren atención</StatLabel>
          <StatValue>{totals.needsAttention}</StatValue>
        </StatTile>
      </StatStrip>

      <TechSection title="Agregar cuenta dummy" style={{ marginBottom: '1.25rem' }}>
        <form onSubmit={handleAdd}>
          <Row cols={4}>
            <TechSelect value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
            </TechSelect>
            <TechInput placeholder="username (sin @)" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            <TechInput placeholder="password" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <TechInput placeholder="display name (opcional)" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          </Row>
          <Row cols={1}>
            <TechInput placeholder="notas (opcional): ej. cuenta vieja con historial, registrada con email X" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Row>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <TechButtonGhost type="button" onClick={load}><FaSyncAlt /> Refrescar</TechButtonGhost>
            <TechButton type="submit" disabled={busy}><FaPlus /> {busy ? '…' : 'Agregar cuenta'}</TechButton>
          </div>
        </form>
      </TechSection>

      <TechSection title={`Cuentas · ${accounts.length}`} style={{ marginBottom: '1.25rem' }}>
        {accounts.length === 0 ? (
          <Empty>Aún no hay cuentas dummy. Agregá una arriba para empezar.</Empty>
        ) : (
          accounts.map((acc) => {
            const meta = PLATFORM_META[acc.platform] || { color: accent, icon: <FaUserSecret />, label: acc.platform };
            const statusMeta = STATUS_META[acc.status] || { tone: 'warn', label: acc.status };
            return (
              <Card key={acc.id} $color={meta.color}>
                <div className="icon">{meta.icon}</div>
                <div>
                  <h4 className="name">{acc.display_name || acc.username}</h4>
                  <div className="meta">
                    {meta.label} · @{acc.username}
                    {' · '}
                    <span className={statusMeta.tone}>
                      {statusMeta.tone === 'ok' && <FaCheckCircle />}
                      {statusMeta.tone === 'err' && <FaExclamationTriangle />}
                      {' '}{statusMeta.label}
                    </span>
                    {acc.health && acc.health.label !== 'unused' && (
                      <> · health <span className={
                        acc.health.label === 'healthy' ? 'ok' :
                        acc.health.label === 'degraded' ? 'warn' : 'err'
                      }>{acc.health.score}%</span></>
                    )}
                    {(acc.requests_24h > 0 || acc.errors_24h > 0) && (
                      <> · {acc.requests_24h} req / {acc.errors_24h} err (24h)</>
                    )}
                    {acc.in_cooldown && (
                      <> · <span className="warn">cooldown hasta {fmtRel(acc.cooldown_until)}</span></>
                    )}
                    {acc.has_sessionid && (
                      <> · sessionid <span className="ok">OK</span> ({fmtRel(acc.sessionid_at)})</>
                    )}
                    {acc.last_error && !acc.in_cooldown && (
                      <> · <span className="err" title={acc.last_error}>error</span></>
                    )}
                    {acc.notes && (
                      <> · <span style={{ fontFamily: 'inherit', textTransform: 'none', letterSpacing: 'normal', color: 'var(--text-secondary)' }}>{acc.notes}</span></>
                    )}
                  </div>
                </div>
                <div className="actions">
                  <TechButtonGhost
                    type="button"
                    onClick={() => handleSetSessionid(acc)}
                    title="Cargar sessionid manual desde browser (recomendado)"
                    style={{ padding: '0.35rem 0.7rem', fontSize: '0.72rem' }}
                  >
                    <FaKey /> Sessionid
                  </TechButtonGhost>
                  <TechButtonGhost
                    type="button"
                    onClick={() => handleTest(acc)}
                    disabled={testingId === acc.id || !acc.has_password}
                    title={!acc.has_password ? 'Sin password' : 'Auto-login (probable fallo, IG cambió crypto)'}
                    style={{ padding: '0.35rem 0.7rem', fontSize: '0.72rem' }}
                  >
                    <FaPlay /> {testingId === acc.id ? '…' : 'Auto-login'}
                  </TechButtonGhost>
                  <TechButtonDanger type="button" onClick={() => handleDelete(acc.id)} style={{ padding: '0.35rem 0.6rem', fontSize: '0.72rem' }}>
                    <FaTrashAlt />
                  </TechButtonDanger>
                </div>
              </Card>
            );
          })
        )}
      </TechSection>

      <div style={{
        marginTop: 14,
        padding: '0.85rem 1rem',
        background: panelDeep,
        border: `1px solid ${borderSoft}`,
        borderLeft: '2px solid var(--alert-warning, #f59e0b)',
        borderRadius: 6,
        fontSize: '0.85rem',
        color: 'var(--text-secondary)',
        lineHeight: 1.55,
      }}>
        <strong style={{ color: 'var(--text-primary)' }}>Sistema anti-baneo activo:</strong>
        <ul style={{ margin: '8px 0 0 1.2rem', padding: 0, fontSize: '0.84rem' }}>
          <li>🛡️ <strong>Proxies Webshare</strong> (10 IPs rotativas, sticky por cuenta) — IG ve cada cuenta desde una IP distinta.</li>
          <li>⏱️ <strong>Rate limit humano</strong>: 90-300s entre requests de la misma cuenta. Si pasa el límite diario (80 req/día), cooldown 4h.</li>
          <li>🌙 <strong>Horario humano</strong>: scraping pausado de 00:00 a 06:00 ART (los humanos duermen).</li>
          <li>🚦 <strong>Cooldown automático</strong>: si una cuenta acumula 5+ errores en 24h, cooldown de 12h.</li>
          <li>👣 <strong>Navigation pattern humano</strong>: antes de pedir comments, visita la página del post como haría un usuario real.</li>
          <li>♻️ <strong>Rotación entre cuentas</strong>: el scraper usa la cuenta con menos uso reciente. Health score visible en cada card.</li>
        </ul>
        <p style={{ margin: '12px 0 0 0', fontSize: '0.82rem', color: 'var(--text-disabled)' }}>
          <strong>Aún así, IG puede banear cuentas en cualquier momento.</strong> Plazo típico con todas las técnicas activas: 5-15 días por cuenta. Para vida más larga (2-6 meses) se necesitan <strong>proxies residenciales pagos</strong> (~USD 6+/mes).
        </p>
      </div>
    </>
  );
};

export default DummyAccountsPanel;
